import React, { useState } from 'react';
import {
    Box,
    Grid,
    Typography,
    FormControlLabel,
    Paper,
    Switch,
    Tooltip,
    IconButton,
    Menu,
    MenuItem,
    Dialog,
    DialogContent
} from '@mui/material';
import { ArrowDropDown, ArrowDropUp, Close } from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid'; // or from '@material-ui/data-grid' based on your version
import Header from "../global/Header";
import styled from '@emotion/styled';
import { useNavigate } from 'react-router-dom';
import { useDiscreteMode } from '../global/DiscreteMode';
import CodeDisplay from './CodeDisplay';
import AdcSettings from './AdcSettings';
import AdcDataGrid from './AdcDataGrid';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'; // For date-fns
import WhitelabelDomains from './WhitelabelDomains'; // Adjust the import path as necessary
import StatExport from './StatExport'; // Adjust the import path as necessary
import IPHemAdjuster from './IPHemAdjuster'; // Adjust the import path as necessary
import { format } from 'date-fns';
import { motion } from 'framer-motion';
import ADCTrafficAnalysis from './ADCTrafficAnalysis';
import { ADCDeliveryConfig } from '../organization/adc_config/ADCDeliveryConfig';

const StyledSwitch = styled(({ checked, ...otherProps }) => (
    <Switch {...otherProps} classes={{ track: checked ? 'checked' : '' }} />
))`
    & .checked {
      background-color: lightgreen;
    }
  `;

const RealTimeSwitch = styled(Switch)`
    & .Mui-checked {
        color: red;
    }
    & .Mui-checked + .MuiSwitch-track {
        background-color: red;
    }
`;

const AdcMetrics = ({
    organization,
    isAdmin,
    viewBotFilteredResults,
    handleToggleSwitch,
    adcRows,
    colors // if it's not a default prop in your project, you should import or define it.
}) => {
    const navigate = useNavigate();
    const { isDiscreteMode } = useDiscreteMode();
    const [viewRealTimeMetrics, setViewRealTimeMetrics] = useState(false);
    const [menuAnchorEl, setMenuAnchorEl] = useState(null);
    const [isSettingsDialogOpen, setIsSettingsDialogOpen] = useState(false);
    const [isDeliveryConfigDialogOpen, setIsDeliveryConfigDialogOpen] = useState(false);
    const [isWhitelabelDialogOpen, setIsWhitelabelDialogOpen] = useState(false);

    const hasRealTimeMetrics = adcRows.some(row =>
        row.hasOwnProperty('visits_resolved_count_real_time') ||
        row.hasOwnProperty('distinct_visits_resolved_count_real_time') ||
        row.hasOwnProperty('visits_resolved_count_bot_filtered_real_time') ||
        row.hasOwnProperty('distinct_visits_resolved_count_bot_filtered_real_time')
    );

    const [whitelabel_config, setWhitelabelConfig] = useState(
        organization.whitelabel_config || [{ host: '', type: '', value: '', purpose: '' }]
    );

    const handleMenuOpen = (event) => {
        setMenuAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setMenuAnchorEl(null);
    };

    const handleSettingsDialogOpen = () => {
        setIsSettingsDialogOpen(true);
        handleMenuClose();
    };

    const handleSettingsDialogClose = () => {
        setIsSettingsDialogOpen(false);
    };

    const handleDeliveryConfigDialogOpen = () => {
        setIsDeliveryConfigDialogOpen(true);
        handleMenuClose();
    };

    const handleDeliveryConfigDialogClose = () => {
        setIsDeliveryConfigDialogOpen(false);
    };

    const handleWhitelabelDialogOpen = () => {
        setIsWhitelabelDialogOpen(true);
        handleMenuClose();
    };

    const handleWhitelabelDialogClose = () => {
        setIsWhitelabelDialogOpen(false);
    };

    return (
        <>
            {(organization.adc_enabled || isAdmin) && (
                <Box m="20px">
                    <Grid container spacing={2} style={{
                        display: 'flex',
                        alignItems: 'stretch',
                        justifyContent: 'space-between', // Adjusted to space-between to push button to the right
                        gap: '8px'
                    }}>
                        <Grid item xs={4}>
                            <Header title="ADC Member Dashboard" />
                        </Grid>
                        <Grid item>
                            <IconButton onClick={handleMenuOpen}>
                                {Boolean(menuAnchorEl) ? <ArrowDropUp /> : <ArrowDropDown />}
                                <Typography variant="p4" color={colors.grey[200]} style={{ marginLeft: '8px' }}>More</Typography>
                            </IconButton>
                            <Menu
                                anchorEl={menuAnchorEl}
                                keepMounted
                                open={Boolean(menuAnchorEl)}
                                onClose={handleMenuClose}
                            >
                                <MenuItem onClick={handleSettingsDialogOpen}>Show ADC Settings</MenuItem>
                                <MenuItem onClick={handleDeliveryConfigDialogOpen}>ADC Delivery Config</MenuItem>
                                <MenuItem onClick={handleWhitelabelDialogOpen}>Whitelabel Domains</MenuItem>
                            </Menu>
                        </Grid>
                    </Grid>

                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <Grid container spacing={1} style={{ display: 'flex', alignItems: 'stretch' }}>
                            <Grid item xs={4}> {/* Adjust xs values to fit your specific layout needs */}
                                <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.5 }}
                                >
                                    <CodeDisplay organization={organization} isDiscreteMode={isDiscreteMode} colors={colors} />
                                </motion.div>
                                <FormControlLabel
                                    control={<StyledSwitch checked={viewBotFilteredResults} onChange={handleToggleSwitch} />}
                                    label={<Typography style={{ color: colors.grey[200] }}>View Bot Filtered ADC Metrics</Typography>}
                                />
                                {hasRealTimeMetrics && (
                                    <FormControlLabel
                                        control={<RealTimeSwitch checked={viewRealTimeMetrics} onChange={() => setViewRealTimeMetrics(!viewRealTimeMetrics)} />}
                                        label={<Typography style={{ color: colors.grey[200] }}>View Real Time Stats</Typography>}
                                    />
                                )}
                            </Grid>
                            <Grid item mb={6} xs={4}><motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5 }}
                            >
                                <ADCTrafficAnalysis organization={organization} isDiscreteMode={isDiscreteMode} colors={colors} />
                            </motion.div>
                            </Grid>

                            {/*
                            {organization.whitelabel_enabled && (
                                <Grid item xs={3}>
                                    <WhitelabelDomains whitelabelConfig={whitelabel_config} />
                                </Grid>
                            )}
                            */}
                            <Grid item xs={2}>
                                {/*<StatExport adcRows={adcRows} adcColumns={adcColumns} />*/}
                                {/*<IPHemAdjuster />*/}
                            </Grid>
                        </Grid>
                    </LocalizationProvider>

                    {/* Check if adcRows is present and not empty */}
                    {adcRows && adcRows.length > 0 && (
                        <AdcDataGrid adcRows={adcRows} viewBotFilteredResults={viewBotFilteredResults} viewRealTimeMetrics={viewRealTimeMetrics} />
                    )}

                    {/* Display a message if adcRows is empty or not present */}
                    {!adcRows || adcRows.length === 0 && (
                        <Typography variant="subtitle1" color="text.secondary" style={{ marginTop: 16 }}>
                            No ADC data available.
                        </Typography>
                    )}
                </Box>
            )}

            {/* ADC Settings Dialog */}
            <Dialog
                open={isSettingsDialogOpen}
                onClose={handleSettingsDialogClose}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    style: {
                        borderRadius: '20px',
                        background: `linear-gradient(135deg, ${colors.primary[500]}, ${colors.primary[800]})`,
                        boxShadow: '0px 0px 10px rgba(0, 0, 0, 1)',
                    },
                }}
            >
                <IconButton
                    aria-label="close"
                    onClick={handleSettingsDialogClose}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: (theme) => theme.palette.grey[500],
                    }}
                >
                    <Close />
                </IconButton>
                <DialogContent>
                    <AdcSettings organization={organization} colors={colors} whitelabel_config={whitelabel_config} />
                </DialogContent>
            </Dialog>

            {/* ADC Delivery Config Dialog */}
            <Dialog
                open={isDeliveryConfigDialogOpen}
                onClose={handleDeliveryConfigDialogClose}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: '20px',
                        background: 'transparent',   // 🔥 remove grey
                        boxShadow: 'none'            // 🔥 remove outer frame
                    }
                }}
            >
                <IconButton
                    aria-label="close"
                    onClick={handleDeliveryConfigDialogClose}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        zIndex: 5,
                        color: (theme) => theme.palette.grey[300],
                    }}
                >
                    <Close />
                </IconButton>

                <DialogContent sx={{ p: 0, background: 'transparent' }}>
                    <ADCDeliveryConfig
                        adc_config={organization.adc_config}
                        organization={organization}
                    />
                </DialogContent>
            </Dialog>

            {/* Whitelabel Domains Dialog */}
            <Dialog open={isWhitelabelDialogOpen} onClose={handleWhitelabelDialogClose} maxWidth="md" fullWidth PaperProps={{ style: { variant: "outlined", borderRadius: '20px', backgroundColor: colors.primary[700], boxShadow: '0px 0px 10px rgba(0, 0, 0, 1)' } }}>
                <IconButton
                    aria-label="close"
                    onClick={handleWhitelabelDialogClose}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: (theme) => theme.palette.grey[500],
                    }}
                >
                    <Close />
                </IconButton>
                <DialogContent>
                    <WhitelabelDomains whitelabelConfig={whitelabel_config} organization={organization} />
                </DialogContent>
            </Dialog>
        </>
    );
};

export default AdcMetrics;
